import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import { CustomFilter } from '@tools/interfaces/custom-filter';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

@Component({
  name: 'bpc-custom-filter-selector',
  components: {
    'bpc-confirmation-modal': ConfirmationModal,
  },
})
export default class CustomFilterSelector extends Vue {
  $refs: {
    deleteCustomFilterConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
  };

  @Prop(String) readonly entity: string;
  @Prop({ type: String, default: 'table' }) readonly storeName: string;
  @Prop(Boolean) readonly notStore: string;
  @Prop(Object) readonly value: Record<string, string | number | object>;
  @Prop(Boolean) readonly pressed: boolean;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly filtersExcluded: string[];

  customFilters: CustomFilter[] = [];

  public systemExcluded = [
    'sortedBy',
    'orderBy',
    'orderDirection',
    'page',
    'per_page',
    'tenantAlias',
    'search',
    'columns',
    'computedFields',
    'relationFields',
    'withRelations',
  ];

  get deleteCustomFilterConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'custom-filter-delete-modal',
      title: this.$t('global.modals.delete_custom_filter.title') as string,
      body: this.$t('global.modals.delete_custom_filter.body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.move_to_trash') as string,
      acceptButtonVariant: 'danger',
    };
  }

  async clickOnConfirmDeleteFilter({ entityIdOrIds }: { entityIdOrIds: number }) {
    console.log('DeleteIds:', entityIdOrIds);
    const delPath = `${this.$bpcApiRouteHelper.getEntityPath('custom-filters')}/${entityIdOrIds}`;
    await this.$bpcApi.delete(delPath).toPromise();
    this.$refs.deleteCustomFilterConfirmationModal.onCloseModal();
    await this.getCustomFilters();
  }

  get userHasGlobalFilterPrivilege(): boolean {
    return false; // TODO: implement the logic if needed
  }

  extendedFilterClicked() {
    this.$emit('extended-filter-clicked');
  }

  async clickOnDeleteFilter(customFilter: CustomFilter) {
    if (this.isOwner(customFilter)) {
      this.$refs.deleteCustomFilterConfirmationModal.onOpenModal(customFilter.id);
    }
  }

  get currentUserId(): number {
    return this.$store.getters['auth/user']?.id || null;
  }

  get ownCustomFilters(): CustomFilter[] {
    return this.customFilters.filter(customFilter => {
      return !customFilter.public && this.isOwner(customFilter);
    });
  }

  get publicCustomFilters(): CustomFilter[] {
    return this.customFilters.filter(customFilter => {
      return customFilter.public;
    });
  }

  isOwner(customFilter): boolean {
    if (this.userHasGlobalFilterPrivilege) {
      return true;
    }
    if (!this.currentUserId) {
      return false;
    }
    return Number(customFilter.user_id) === Number(this.currentUserId);
  }

  clickFilter(customFilter: CustomFilter) {
    this.filterModel = customFilter.filters;
    this.$emit('on-change-filter', customFilter.filters);
  }

  async getCustomFilters() {
    const filters = {
      entity: this.entity,
      publicOrOwned: true,
    };

    const res = await this.$bpcApi
      .get(this.$bpcApiRouteHelper.getEntityPath('custom-filters'), { params: filters })
      .toPromise();

    this.customFilters = res.data;
    return res;
  }

  get allFiltersExcluded() {
    return this.systemExcluded.concat(this.filtersExcluded);
  }

  get countFilters(): number {
    return this.filters.length;
  }

  get filters() {
    return Object.keys(this.filterModel).reduce((filters, key) => {
      const isNully =
        this.filterModel[key] === null ||
        this.filterModel[key] === undefined ||
        this.filterModel[key] === '' ||
        this.filterModel[key].length === 0;
      if (!isNully && !this.allFiltersExcluded.includes(key)) {
        filters.push({
          name: key,
          value: this.filterModel[key],
          valueLabel: '',
        });
      }
      return filters;
    }, []);
  }

  get filterModel() {
    return this.notStore
      ? this.value
      : this.$store.getters[`${this.storeName}/filterModel`](this.entity) || {};
  }
  set filterModel(value) {
    if (this.notStore) {
      this.$emit('input', value);
    } else {
      this.$store.commit(`${this.storeName}/setFilterModel`, {
        entity: this.entity,
        model: value,
      });
    }
  }

  setNullFilterModel(key) {
    const newFilterModel = { ...this.filterModel };
    newFilterModel[key] = null;
    this.filterModel = newFilterModel;

    return newFilterModel;
  }

  onClearFilters() {
    this.filters.forEach(filter => {
      this.setNullFilterModel(filter.name);
    });
    this.$emit('filters-cleared', this.filterModel);
    this.$emit('on-change-filter', this.filterModel);
  }
  /* Lifecycle hooks */
  // created () { }
  mounted() {
    this.getCustomFilters();
  }
  // updated () { }
  // destroyed () { }
}
